import React, { useMemo, useState } from 'react'
import { formatGrooverText } from '../../libs/GrooverTextRepository'
import { getFunctionUrl } from '../../libs/FunctionsUrlHelper'
import { buildRoute } from '../../../../pure-js/libs/RouteBuilder'
import RoutePath from '../../../../pure-js/libs/RoutePath'
import { CloudFunction } from '../../../../pure-js/enums/CloudFunction'
import { DropinTicket, DropinTicketStatus } from '../../../../pure-js/types/GrooverTypes'
import QRCodeElement from '../../components/QRCodeElement'
import { QrExpandedDialog } from '../../components/qr/QrExpandedDialog'
import { AddToWalletButtons } from '../../components/AddToWalletButtons'
import { PortalDropinTicketPassesHero } from './PortalDropinTicketCard'
import type { PortalDropinGetText } from './PortalDropinTicketCard'
import {
  formatDate,
  formatDateTime,
  formatMinutesAgo,
  formatPrice,
  getLastConsumeLog,
  getStatusLabel,
  isExpiringWithin30Days,
  isRecentlyActivated
} from './portalDropinTicketHelpers'
import {
  ClipUsedBadge,
  DetailCard,
  DetailCardCloseButton,
  DetailCardCloseButtonGreen,
  DetailCardContent,
  DetailCardRecentlyActivated,
  DetailCardTitle,
  DetailCloseButton,
  DetailDetailsSection,
  DetailList,
  DetailQRSection,
  DetailQRSectionCompact,
  DetailQRWrapperClickable,
  DetailQRWrapperCompactClickable,
  DetailRow,
  DetailRowLabel,
  DetailRowValue,
  DetailSchoolSubtitle,
  DetailTopBar,
  DetailTopBarLeft,
  DetailViewReceiptLink,
  DetailWalletSection,
  ExpiringSoonBadge,
  LastActivatedOkLabel,
  LastActivatedSection,
  LastActivatedText,
  LastUsedDateTime,
  LastUsedItem,
  LastUsedItemLine,
  LastUsedSchool,
  LastUsedSection,
  LastUsedTitle,
  SectionLabel,
  StatusPill
} from './PortalDropinTicketsPage.styles'

interface PortalDropinTicketDetailCardProps {
  ticket: DropinTicket
  enrollmentUserId: string | undefined
  getText: PortalDropinGetText
  selectedLanguage: string
  onClose: () => void
}

export const PortalDropinTicketDetailCard: React.FC<PortalDropinTicketDetailCardProps> = ({
  ticket,
  enrollmentUserId,
  getText,
  selectedLanguage,
  onClose
}) => {
  const [showQrExpanded, setShowQrExpanded] = useState(false)
  const ticketsRemaining = ticket.ticketCount - ticket.ticketsUsed
  const qrCodeValue = `d/${ticket.id}`
  const showWalletButtons = ticket.status === DropinTicketStatus.Active || ticket.status === DropinTicketStatus.UsedUp
  const appleWalletUrl = showWalletButtons
    ? `${getFunctionUrl(CloudFunction.appleWalletDropinPass)}?ticketId=${encodeURIComponent(ticket.id)}&clientId=${encodeURIComponent(ticket.clientId)}`
    : null
  const googleWalletUrl = showWalletButtons
    ? `${getFunctionUrl(CloudFunction.googleWalletDropinPass)}?ticketId=${encodeURIComponent(ticket.id)}&clientId=${encodeURIComponent(ticket.clientId)}`
    : null
  const receiptUrl =
    enrollmentUserId && ticket.id
      ? buildRoute(RoutePath.DROPIN_TICKET_DETAIL, {
          enrollmentUserId,
          dropinTicketId: ticket.id
        })
      : null

  const lastConsumeLog = useMemo(() => getLastConsumeLog(ticket), [ticket])
  const recentlyActivated = lastConsumeLog !== null && isRecentlyActivated(lastConsumeLog)
  const CardWrapper = recentlyActivated ? DetailCardRecentlyActivated : DetailCard
  const expiringSoon = isExpiringWithin30Days(ticket.validUntil)
  const consumeLogsModal = useMemo(() => {
    const list = (ticket.logs || []).filter((log) => log.type === 'consume')
    return [...list].sort((a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()).slice(0, 3)
  }, [ticket.logs])

  const ticketDetailsSection = (
    <DetailDetailsSection>
      <SectionLabel>{getText('portalDropinTicketDetailsLabel')}</SectionLabel>
      <DetailList>
        <DetailRow>
          <DetailRowLabel>{getText('portalDropinTicketValidUntil')}</DetailRowLabel>
          <DetailRowValue
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              justifyContent: 'flex-end'
            }}
          >
            {formatDate(ticket.validUntil)}
            {expiringSoon && <ExpiringSoonBadge>{getText('portalDropinTicketExpiringSoon')}</ExpiringSoonBadge>}
          </DetailRowValue>
        </DetailRow>
        <DetailRow>
          <DetailRowLabel>{getText('portalDropinTicketPrice')}</DetailRowLabel>
          <DetailRowValue>{formatPrice(ticket.price)}</DetailRowValue>
        </DetailRow>
        <DetailRow>
          <DetailRowLabel>{getText('portalDropinTicketCount')}</DetailRowLabel>
          <DetailRowValue>
            {formatGrooverText(getText('portalDropinTicketCountValue'), [
              ticket.ticketCount.toString(),
              ticket.ticketsUsed.toString()
            ])}
          </DetailRowValue>
        </DetailRow>
        <DetailRow>
          <DetailRowLabel>{getText('portalDropinTicketRemaining')}</DetailRowLabel>
          <DetailRowValue>{ticketsRemaining}</DetailRowValue>
        </DetailRow>
      </DetailList>
    </DetailDetailsSection>
  )

  const detailHeader = (
    <>
      <DetailTopBar>
        <DetailTopBarLeft>
          <StatusPill status={ticket.status}>{getStatusLabel(ticket.status, getText)}</StatusPill>
          {expiringSoon && <ExpiringSoonBadge>{getText('portalDropinTicketExpiringSoon')}</ExpiringSoonBadge>}
        </DetailTopBarLeft>
        <DetailSchoolSubtitle>{ticket.schoolName}</DetailSchoolSubtitle>
      </DetailTopBar>
      <DetailCardTitle id="dropin-ticket-detail-title">
        {ticket.packageName || getText('portalDropinTicketPackage')}
      </DetailCardTitle>
      <PortalDropinTicketPassesHero ticket={ticket} getText={getText} />
    </>
  )

  const lastUsedSection =
    consumeLogsModal.length > 0 ? (
      <LastUsedSection style={{ marginTop: 20 }}>
        <LastUsedTitle>{getText('portalDropinTicketLastUsed')}</LastUsedTitle>
        {consumeLogsModal.map((log) => (
          <LastUsedItem key={log.id}>
            <LastUsedItemLine>
              <LastUsedSchool>{log.clientName || ticket.schoolName}</LastUsedSchool>
              <ClipUsedBadge>{formatGrooverText(getText('portalDropinTicketClipUsed'), ['1'])}</ClipUsedBadge>
            </LastUsedItemLine>
            <LastUsedDateTime>{formatDateTime(log.timestamp)}</LastUsedDateTime>
          </LastUsedItem>
        ))}
      </LastUsedSection>
    ) : null

  return (
    <>
      <CardWrapper onClick={(e) => e.stopPropagation()}>
        {recentlyActivated ? (
          <DetailCardCloseButtonGreen type="button" onClick={onClose} aria-label={getText('portalDropinTicketClose')}>
            ×
          </DetailCardCloseButtonGreen>
        ) : (
          <DetailCardCloseButton type="button" onClick={onClose} aria-label={getText('portalDropinTicketClose')}>
            ×
          </DetailCardCloseButton>
        )}
        <DetailCardContent>
          {recentlyActivated && lastConsumeLog ? (
            <>
              <LastActivatedSection>
                <LastActivatedText>
                  {getText('portalDropinTicketLastActivatedPrefix')}
                  <span style={{ whiteSpace: 'nowrap' }}>
                    {formatMinutesAgo(lastConsumeLog.timestamp, selectedLanguage)}
                  </span>
                  {getText('portalDropinTicketLastActivatedSuffix')}
                </LastActivatedText>
                <LastActivatedOkLabel>{getText('portalDropinTicketOkScreen')}</LastActivatedOkLabel>
              </LastActivatedSection>
              {detailHeader}
              <DetailQRSectionCompact>
                <span style={{ fontSize: 12, color: '#6b7280' }}>{getText('portalDropinTicketShowQrAtStudio')}</span>
                <DetailQRWrapperCompactClickable
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowQrExpanded(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowQrExpanded(true)}
                  aria-label={getText('portalDropinTicketTapToEnlarge')}
                >
                  <QRCodeElement value={qrCodeValue} showText={false} />
                </DetailQRWrapperCompactClickable>
                <span style={{ fontSize: 11, color: '#9ca3af', textAlign: 'center', lineHeight: 1.4 }}>
                  {getText('portalDropinTicketAutoUpdatesOnCheckin')}
                </span>
              </DetailQRSectionCompact>
            </>
          ) : (
            <>
              {detailHeader}
              <DetailQRSection>
                <span style={{ fontSize: 14, color: '#6b7280' }}>{getText('portalDropinTicketShowQrAtStudio')}</span>
                <DetailQRWrapperClickable
                  role="button"
                  tabIndex={0}
                  onClick={() => setShowQrExpanded(true)}
                  onKeyDown={(e) => e.key === 'Enter' && setShowQrExpanded(true)}
                  aria-label={getText('portalDropinTicketTapToEnlarge')}
                >
                  <QRCodeElement value={qrCodeValue} showText={false} />
                </DetailQRWrapperClickable>
                <span style={{ fontSize: 12, color: '#9ca3af', textAlign: 'center', lineHeight: 1.4 }}>
                  {getText('portalDropinTicketAutoUpdatesOnCheckin')}
                </span>
              </DetailQRSection>
            </>
          )}

          {showWalletButtons && (
            <DetailWalletSection>
              <SectionLabel>{getText('portalDropinTicketAddToWalletLabel')}</SectionLabel>
              <AddToWalletButtons appleWalletUrl={appleWalletUrl} googleWalletUrl={googleWalletUrl} />
            </DetailWalletSection>
          )}

          {ticketDetailsSection}
          {lastUsedSection}

          {receiptUrl && (
            <DetailViewReceiptLink href={receiptUrl} target="_blank" rel="noopener noreferrer">
              {getText('portalDropinTicketViewFullReceipt')}
            </DetailViewReceiptLink>
          )}
          <DetailCloseButton type="button" onClick={onClose}>
            {getText('portalDropinTicketClose')}
          </DetailCloseButton>
        </DetailCardContent>
      </CardWrapper>

      <QrExpandedDialog
        open={showQrExpanded}
        value={qrCodeValue}
        onClose={() => setShowQrExpanded(false)}
        closeLabel={getText('portalDropinTicketClose')}
        instruction={getText('portalDropinTicketShowQrAtStudio')}
        labelledBy="portal-dropin-detail-qr"
      />
    </>
  )
}
